import type { UseFormReturn } from "react-hook-form";
import { calculatedArtworkDimensions } from "@design-lab/entities/artwork/lib/calculateDimensions";
import type { CachedImage } from "@design-lab/entities/artwork/model/useImagesFastCache";
import type { ArtworkMachine } from "@design-lab/entities/artwork/model/useArtwork";
import type { Artwork } from "@design-lab/shared/types/artwork";
import type { ArtworkForm } from "@design-lab/shared/types";
import type { printOnDemand } from "common/utils/models";
import { normalizeArtworkResponse } from "@design-lab/shared/lib/enhancementPolling";

type PrintOnDemandComposition = printOnDemand.getProduct.Composition;

type ArtworkMachineRef = Pick<ArtworkMachine, "send">;

export type ArtworkApiResponse = {
  id: number;
  currentEnhancementState?: string;
  original?: {
    imageUrl?: string;
  };
  ordered?: {
    imageUrl?: string;
    noBgUrl?: string;
    withBgUrl?: string;
    noBgNoTrimUrl?: string;
    manualEditUrl?: string;
    manualEditNoTrimUrl?: string;
    canRemoveBg?: boolean;
    removeBackground?: boolean;
    enhanceImage?: boolean;
    enhancementTier?: string;
    metadata?: {
      error?: string;
    };
  };
};

/**
 * Processes raw artwork API response into a consistent format
 */
export function processArtworkData(artwork: ArtworkApiResponse): Artwork {
  return {
    ...artwork.ordered,
    ...normalizeArtworkResponse(artwork)
  } as unknown as Artwork;
}

/**
 * Sets up artwork form data for a given side/placement
 */
export function setupArtworkForm(
  form: UseFormReturn<ArtworkForm>,
  options: {
    artwork: Artwork;
    side: "front" | "back";
    cachedImage: CachedImage | undefined;
    composition: PrintOnDemandComposition | undefined;
  }
) {
  const { artwork, side, cachedImage, composition } = options;
  form.setValue("currentPlacement", side);

  if (side === "back") {
    const surface = composition?.surfaces.back;
    const dims = calculatedArtworkDimensions(cachedImage?.image, surface);

    form.setValue("back.size", {
      width: dims.maxWidthInches,
      height: dims.maxHeightInches
    });
    form.setValue("back.artworkId", String(artwork.id));
    form.setValue("info.back.enhancementTier", "full_enhancements");
    form.setValue("info.back.removeBackground", true);
  } else {
    // Handle front placement
    const frontSurface = composition?.surfaces.front;
    const dimsFront = calculatedArtworkDimensions(cachedImage?.image, frontSurface);

    form.setValue("front.size", {
      width: dimsFront.maxWidthInches,
      height: dimsFront.maxHeightInches
    });

    const chestSurface = composition?.surfaces.frontLeftChest;
    const dimsChest = calculatedArtworkDimensions(cachedImage?.image, chestSurface);

    form.setValue("frontLeftChest.size", {
      width: dimsChest.maxWidthInches,
      height: dimsChest.maxHeightInches
    });

    form.setValue("front.artworkId", String(artwork.id));
    form.setValue("frontLeftChest.artworkId", String(artwork.id));
    form.setValue("info.front.enhancementTier", "full_enhancements");
    form.setValue("info.front.removeBackground", true);
  }
}

/**
 * Sets up artwork state machine based on enhancement state
 */
export function setupArtworkMachine(
  machine: ArtworkMachineRef,
  artwork: Artwork,
  enhancementState?: string
) {
  if (enhancementState === "complete_processing") {
    // Enhancement is complete - render final artwork
    machine.send({
      type: "reset",
      targetState: "finished",
      context: {
        visitedStates: ["progress", "uploaded", "enhancing", "finished"],
        artworkId: Number(artwork.id),
        artwork,
        cacheWarmingStatus: "none"
      }
    });
  } else {
    // Still processing - show original image and listen for websocket updates
    machine.send({
      type: "reset",
      context: {
        visitedStates: ["progress"],
        artworkId: Number(artwork.id),
        artwork,
        source: "design_library" as const,
        startTime: Date.now(),
        cacheWarmingStatus: "none"
      }
    });

    // Transition to enhancing state to listen for websocket updates
    machine.send({ type: "enhancement.started" });
  }
}
